import React, { useState } from "react";
import { Box, TextField, IconButton, Typography, Button, Menu, MenuItem, ListItemText } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

interface Contact {
  name: string;
  email: string;
  phone: string;
}

interface ContactSectionProps {
  title: string;
  description: string;
  contacts: Contact[];
  errors: Array<{
    name?: string;
    email?: string;
    phone?: string;
  }>;
  onContactChange: (index: number, field: keyof Contact, value: string) => void;
  onAddContact: () => void;
  onRemoveContact: (index: number) => void;
  submitting: boolean;
  sectionKey: string;
  availableContacts: {
    hr?: Contact[];
    finance?: Contact[];
    payroll?: Contact[];
  };
  onCopyFrom: (contactData: Contact) => void;
}

const ContactSection: React.FC<ContactSectionProps> = ({
  title,
  description,
  contacts,
  errors,
  onContactChange,
  onAddContact,
  onRemoveContact,
  submitting,
  sectionKey,
  availableContacts,
  onCopyFrom,
}) => {
  const [copyMenuAnchor, setCopyMenuAnchor] = useState<null | HTMLElement>(null);

  // Get available contacts from other sections
  const getAvailableContacts = () => {
    const contactMap = new Map<string, {
      name: string;
      email: string;
      phone: string;
      sections: string[];
    }>();

    const addContactsToMap = (contactList: Contact[] | undefined, sectionName: string) => {
      if (!contactList) return;
      contactList.forEach((contact) => {
        if (contact.name.trim() && contact.email.trim()) {
          if (contactMap.has(contact.email)) {
            const existing = contactMap.get(contact.email)!;
            if (!existing.sections.includes(sectionName)) {
              existing.sections.push(sectionName);
            }
          } else {
            contactMap.set(contact.email, {
              ...contact,
              sections: [sectionName],
            });
          }
        }
      });
    };

    if (sectionKey !== 'hr') {
      addContactsToMap(availableContacts.hr, 'HR');
    }
    if (sectionKey !== 'finance') {
      addContactsToMap(availableContacts.finance, 'Finance');
    }
    if (sectionKey !== 'payroll') {
      addContactsToMap(availableContacts.payroll, 'Payroll');
    }

    return Array.from(contactMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((contact) => ({
        ...contact,
        section: contact.sections.join(' & '),
      }));
  };

  const availableContactsList = getAvailableContacts();

  const handleCopyMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCopyMenuAnchor(event.currentTarget);
  };

  const handleCopyMenuClose = () => {
    setCopyMenuAnchor(null);
  };

  const handleCopyContact = (contact: Contact & { section: string }) => {
    onCopyFrom(contact);
    handleCopyMenuClose();
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {/* Add Existing Contact Button */}
      <Box sx={{ mb: 2, position: 'relative' }}>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleCopyMenuOpen}
          disabled={submitting || availableContactsList.length === 0}
          sx={{
            width: '100%',
            justifyContent: 'center',
            borderStyle: 'dashed',
          }}
        >
          Add existing contact
        </Button>
        <Menu
          anchorEl={copyMenuAnchor}
          open={Boolean(copyMenuAnchor)}
          onClose={handleCopyMenuClose}
          PaperProps={{
            sx: {
              maxHeight: 300,
              width: '100%',
              maxWidth: 400,
            },
          }}
        >
          {availableContactsList.length > 0 ? (
            availableContactsList.map((contact, idx) => (
              <MenuItem
                key={idx}
                onClick={() => handleCopyContact(contact)}
              >
                <ListItemText
                  primary={contact.name}
                  secondary={contact.section}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText primary="No contacts available to copy" />
            </MenuItem>
          )}
        </Menu>
      </Box>

      {contacts.map((contact, index) => (
        <Box
          key={index}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            mb: 2,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {title.split(" ")[0]} Person {index + 1}
            </Typography>
            {(index > 0 || contacts.length > 1) && (
              <IconButton
                size="small"
                onClick={() => onRemoveContact(index)}
                disabled={submitting}
                sx={{ color: "error.main" }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>

          <TextField
            fullWidth
            label="Full Name"
            value={contact.name}
            onChange={(e) => onContactChange(index, "name", e.target.value)}
            error={!!errors[index]?.name}
            helperText={errors[index]?.name}
            required
            sx={{ mb: 2 }}
            disabled={submitting}
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={contact.email}
            onChange={(e) => onContactChange(index, "email", e.target.value)}
            error={!!errors[index]?.email}
            helperText={errors[index]?.email}
            required
            sx={{ mb: 2 }}
            disabled={submitting}
          />

          <TextField
            fullWidth
            label="Phone Number"
            value={contact.phone}
            onChange={(e) => onContactChange(index, "phone", e.target.value)}
            error={!!errors[index]?.phone}
            helperText={errors[index]?.phone}
            sx={{ mb: 2 }}
            disabled={submitting}
          />
        </Box>
      ))}

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <IconButton
          onClick={onAddContact}
          disabled={submitting}
          sx={{
            border: "1px dashed",
            borderColor: "primary.main",
            color: "primary.main",
            "&:hover": {
              borderColor: "primary.dark",
              backgroundColor: "primary.light",
            },
          }}
        >
          <Add />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ContactSection;


